import { useCallback, useEffect, useRef, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

import { useWebSocket } from '@hooks/useWebSocket'

type MarketDataTick = {
  Timestamp: string
  Symbol: string
  Bid: number
  Ask: number
}

export type MarketDataTicksMessage = {
  $type: 'TicksMarketData'
  Ticks: MarketDataTick[]
}

type MarketDataCandle = {
  Timestamp: string
  Symbol: string
  CandleType: string
  OpeningPrice: number | null
  ClosingPrice: number | null
  HighPrice: number | null
  LowPrice: number | null
}

export type MarketDataCandlesMessage = {
  $type: 'CandlesMarketData'
  Candles: MarketDataCandle[]
}

export type MarketDataMessage =
  | MarketDataTicksMessage
  | MarketDataCandlesMessage
  | { [key: string]: unknown }

export const isTicksMarketDataMessage = (
  message: MarketDataMessage
): message is MarketDataTicksMessage => {
  return message?.$type === 'TicksMarketData'
}

type UseMarketDataWebSocketOptions = {
  symbols?: string[]
  onTick?: (message: MarketDataMessage) => void
}

type UseMarketDataWebSocketResult = {
  readyState: ReadyState
  sendMessage: (data: string) => void

  subscribeTicks: (symbols: string[]) => void
  unsubscribeTicks: (symbols: string[]) => void

  subscribeCandles: (
    payload: {
      Symbol: string
      CandleTypeId: string
    }[]
  ) => void

  unsubscribeCandles: (
    payload: {
      Symbol: string
      CandleTypeId: string
    }[]
  ) => void
}

const DEFAULT_MARKETDATA_PATH = 'platform-marketdata'

export function useMarketDataWebSocket(
  options?: UseMarketDataWebSocketOptions
): UseMarketDataWebSocketResult {
  const { symbols, onTick } = options || {}
  const path = DEFAULT_MARKETDATA_PATH

  const [lastSubscribed, setLastSubscribed] = useState<string[]>([])
  const lastSubscribedRef = useRef<string[]>([])
  const readyStateRef = useRef<ReadyState>(ReadyState.CLOSED)

  const { sendMessage, readyState } = useWebSocket(path, {
    share: true,
    heartbeat: false,
    onMessage: (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        onTick?.(data)
      } catch {
        onTick?.(event.data)
      }
    },
  })

  const subscribeTicks = useCallback(
    (symbolsArg: string[]) => {
      if (!symbolsArg || symbolsArg.length === 0) return
      sendMessage(
        JSON.stringify({
          $type: 'SubscribeTicksMarketData',
          Symbols: symbolsArg,
        })
      )
    },
    [sendMessage]
  )

  const unsubscribeTicks = useCallback(
    (symbolsArg: string[]) => {
      if (!symbolsArg || symbolsArg.length === 0) return
      sendMessage(
        JSON.stringify({
          $type: 'UnsubscribeTicksMarketData',
          Symbols: symbolsArg,
        })
      )
    },
    [sendMessage]
  )

  const subscribeCandles = useCallback(
    (symbolCandles: { Symbol: string; CandleTypeId: string }[]) => {
      if (!symbolCandles.length) return
      sendMessage(
        JSON.stringify({
          $type: 'SubscribeCandlesMarketData',
          SymbolCandles: symbolCandles,
        })
      )
    },
    [sendMessage]
  )

  const unsubscribeCandles = useCallback(
    (symbolCandles: { Symbol: string; CandleTypeId: string }[]) => {
      if (!symbolCandles.length) return
      sendMessage(
        JSON.stringify({
          $type: 'UnsubscribeCandlesMarketData',
          SymbolCandles: symbolCandles,
        })
      )
    },
    [sendMessage]
  )

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return

    const nextSymbols = Array.from(new Set(symbols || []))

    if (nextSymbols.length === 0) {
      if (lastSubscribed.length > 0) {
        unsubscribeTicks(lastSubscribed)
        setLastSubscribed([])
      }
      return
    }

    if (JSON.stringify(nextSymbols) === JSON.stringify(lastSubscribed)) {
      return
    }

    const toUnsubscribe = lastSubscribed.filter((s) => !nextSymbols.includes(s))
    const toSubscribe = nextSymbols.filter((s) => !lastSubscribed.includes(s))

    if (toUnsubscribe.length > 0) unsubscribeTicks(toUnsubscribe)
    if (toSubscribe.length > 0) subscribeTicks(toSubscribe)

    setLastSubscribed(nextSymbols)
  }, [symbols, readyState, lastSubscribed, unsubscribeTicks, subscribeTicks])

  useEffect(() => {
    lastSubscribedRef.current = lastSubscribed
  }, [lastSubscribed])

  useEffect(() => {
    readyStateRef.current = readyState
  }, [readyState])

  useEffect(() => {
    return () => {
      if (readyStateRef.current === ReadyState.OPEN && lastSubscribedRef.current.length > 0) {
        unsubscribeTicks(lastSubscribedRef.current)
      }
    }
  }, [unsubscribeTicks])

  return {
    readyState,
    sendMessage,

    subscribeTicks,
    unsubscribeTicks,

    subscribeCandles,
    unsubscribeCandles,
  }
}
